import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import {
  AdminService,
  ContactSubmission,
  ContactSubmissionsFilters,
  ContactSubmissionDetails,
  ContactStats,
  AdminUser,
} from '../../services/admin.service';

@Component({
  selector: 'app-admin-contact-support',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './admin-contact-support.component.html',
  styleUrl: './admin-contact-support.component.css',
})
export class AdminContactSupportComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  submissions: ContactSubmission[] = [];
  selectedSubmissions: Set<string> = new Set();
  availableAdmins: AdminUser[] = [];
  stats: ContactStats | null = null;

  loading = true;
  error: string | null = null;

  currentPage = 1;
  totalPages = 1;
  totalItems = 0;
  itemsPerPage = 20;

  filters: ContactSubmissionsFilters = {
    status: '',
    priority: '',
    assignedTo: '',
  };

  searchTerm = '';

  statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'closed', label: 'Closed' },
  ];

  priorityOptions = [
    { value: '', label: 'All Priorities' },
    { value: 'urgent', label: 'Urgent' },
    { value: 'high', label: 'High' },
    { value: 'normal', label: 'Normal' },
    { value: 'low', label: 'Low' },
  ];

  showBulkActions = false;
  bulkProcessing = false;

  showResponseModal = false;
  showBulkUpdateModal = false;
  showSubmissionDetailsModal = false;

  responseText = '';
  responseType: 'email' | 'phone' | 'internal_note' = 'email';
  emailSubject = '';
  isSentToCustomer = true;

  bulkUpdateData = {
    status: '',
    priority: '',
    assigned_to: '',
    internal_notes: '',
  };

  selectedSubmissionForResponse: ContactSubmission | null = null;
  selectedSubmissionDetails: ContactSubmissionDetails | null = null;

  processing = false;

  messages: {
    success: string | null;
    error: string | null;
  } = {
    success: null,
    error: null,
  };

  constructor(private adminService: AdminService, private router: Router) {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((searchTerm) => {
        this.filters.search = searchTerm || undefined;
        this.currentPage = 1;
        this.loadSubmissions();
      });
  }

  ngOnInit(): void {
    this.loadSubmissions();
    this.loadAvailableAdmins();
    this.loadStats();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadSubmissions(): void {
    this.loading = true;
    this.error = null;

    this.adminService
      .getContactSubmissions(this.filters, this.currentPage, this.itemsPerPage)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.submissions = response.data.submissions || [];
          this.currentPage = response.data.pagination.currentPage || 1;
          this.totalPages = response.data.pagination.totalPages || 1;
          this.totalItems = response.data.pagination.totalItems || 0;
          this.loading = false;
          this.clearSelection();
        },
        error: (error) => {
          console.error('Error loading contact submissions:', error);
          this.error = error.message || 'Failed to load contact submissions';
          this.submissions = [];
          this.totalPages = 1;
          this.totalItems = 0;
          this.currentPage = 1;
          this.loading = false;
        },
      });
  }

  loadAvailableAdmins(): void {
    this.adminService
      .getAvailableAdmins()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (admins) => {
          this.availableAdmins = admins;
        },
        error: (error) => {
          console.error('Error loading admins:', error);
        },
      });
  }

  loadStats(): void {
    this.adminService
      .getContactStats()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (stats) => {
          this.stats = stats;
        },
        error: (error) => {
          console.error('Error loading contact stats:', error);
        },
      });
  }

  onSearch(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
    this.searchSubject.next(this.searchTerm);
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.clearSelection();
    this.loadSubmissions();
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.clearSelection();
      this.loadSubmissions();
    }
  }

  onSubmissionSelect(submissionId: string, event: Event): void {
    event.stopPropagation();

    if (this.selectedSubmissions.has(submissionId)) {
      this.selectedSubmissions.delete(submissionId);
    } else {
      this.selectedSubmissions.add(submissionId);
    }

    this.updateBulkActionsVisibility();
  }

  onSelectAll(event: Event): void {
    const checkbox = event.target as HTMLInputElement;

    if (checkbox.checked) {
      this.submissions.forEach((submission) => {
        this.selectedSubmissions.add(submission.id);
      });
    } else {
      this.clearSelection();
    }

    this.updateBulkActionsVisibility();
  }

  clearSelection(): void {
    this.selectedSubmissions.clear();
    this.updateBulkActionsVisibility();
  }

  updateBulkActionsVisibility(): void {
    this.showBulkActions = this.selectedSubmissions.size > 0;
  }

  isSelected(submissionId: string): boolean {
    return this.selectedSubmissions.has(submissionId);
  }

  isAllSelected(): boolean {
    return (
      this.submissions.length > 0 &&
      this.submissions.every((submission) =>
        this.selectedSubmissions.has(submission.id)
      )
    );
  }

  viewSubmissionDetails(submission: ContactSubmission): void {
    this.adminService
      .getContactSubmissionById(submission.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (details) => {
          this.selectedSubmissionDetails = details;
          this.showSubmissionDetailsModal = true;
        },
        error: (error) => {
          console.error('Error loading submission details:', error);
          this.showErrorMessage('Failed to load submission details');
        },
      });
  }

  respondToSubmission(submission: ContactSubmission): void {
    this.selectedSubmissionForResponse = submission;
    this.responseText = '';
    this.emailSubject = `Re: ${submission.subject || 'Your inquiry'}`;
    this.responseType = 'email';
    this.isSentToCustomer = true;
    this.showResponseModal = true;
  }

  confirmResponse(): void {
    if (!this.selectedSubmissionForResponse || !this.responseText.trim()) {
      return;
    }

    this.processing = true;

    const responseData = {
      responseText: this.responseText.trim(),
      responseType: this.responseType,
      isSentToCustomer: this.isSentToCustomer,
      emailSubject:
        this.responseType === 'email' && this.isSentToCustomer
          ? this.emailSubject
          : undefined,
    };

    this.adminService
      .addContactResponse(this.selectedSubmissionForResponse.id, responseData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showSuccessMessage('Response sent successfully');
          this.closeResponseModal();
          this.processing = false;
          this.loadSubmissions();
          this.loadStats();
        },
        error: (error) => {
          console.error('Error sending response:', error);
          this.showErrorMessage(error.message || 'Failed to send response');
          this.processing = false;
        },
      });
  }

  quickUpdateStatus(submission: ContactSubmission, status: string): void {
    if (!status || status.trim() === '') return;

    this.processing = true;

    const updates: any = { status };
    if (status === 'resolved') {
      updates.resolved_at = new Date().toISOString();
    }

    this.adminService
      .updateContactSubmission(submission.id, updates)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showSuccessMessage(
            `Status updated to ${this.adminService.formatStatus(status)}`
          );
          this.processing = false;
          this.loadSubmissions();
          this.loadStats();
        },
        error: (error) => {
          console.error('Error updating status:', error);
          this.showErrorMessage('Failed to update status');
          this.processing = false;
        },
      });
  }

  onStatusChange(submission: ContactSubmission, event: Event): void {
    const target = event.target as HTMLSelectElement;
    if (target && target.value) {
      this.quickUpdateStatus(submission, target.value);
      target.value = '';
    }
  }

  assignToAdmin(submission: ContactSubmission, adminId: string): void {
    this.processing = true;

    const updates: any = { assigned_to: adminId || null };
    if (adminId) {
      updates.status = 'in_progress';
    }

    this.adminService
      .updateContactSubmission(submission.id, updates)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          const admin = this.availableAdmins.find((a) => a.id === adminId);
          const message = adminId
            ? `Assigned to ${admin?.first_name} ${admin?.last_name}`
            : 'Assignment removed';
          this.showSuccessMessage(message);
          this.processing = false;
          this.loadSubmissions();
        },
        error: (error) => {
          console.error('Error assigning submission:', error);
          this.showErrorMessage('Failed to assign submission');
          this.processing = false;
        },
      });
  }

  getResponseAuthorDisplay(response: any): string {
    if (response.response_author_type === 'admin') {
      return response.author_name || 'Admin';
    } else if (
      response.response_author_type === 'user' ||
      response.response_author_type === 'user_legacy'
    ) {
      return response.author_name || 'Customer';
    } else {
      return 'System';
    }
  }

  getResponseTypeLabel(response: any): string {
    switch (response.response_author_type) {
      case 'admin':
        return response.response_type === 'email'
          ? 'Email Response'
          : response.response_type === 'phone'
          ? 'Phone Note'
          : 'Internal Note';
      case 'user':
      case 'user_legacy':
        return 'Customer Reply';
      default:
        return 'System Message';
    }
  }

  getResponseTypeBadgeClass(response: any): string {
    switch (response.response_author_type) {
      case 'admin':
        return 'badge-admin';
      case 'user':
      case 'user_legacy':
        return 'badge-user';
      default:
        return 'badge-system';
    }
  }

  getResponseText(response: any): string {
    return response.clean_response_text || response.response_text || '';
  }

  hasUserReplies(submission: ContactSubmission): boolean {
    return !!(submission as any).last_user_response;
  }

  bulkUpdate(): void {
    this.showBulkUpdateModal = true;
  }

  confirmBulkUpdate(): void {
    if (this.selectedSubmissions.size === 0 || this.bulkProcessing) {
      return;
    }

    const updates: any = {};
    Object.keys(this.bulkUpdateData).forEach((key) => {
      const value =
        this.bulkUpdateData[key as keyof typeof this.bulkUpdateData];

      if (value && value.trim()) {
        if (key === 'assigned_to' && value.trim() === 'null') {
          updates[key] = null;
        } else {
          updates[key] = value.trim();
        }
      }
    });

    if (Object.keys(updates).length === 0) {
      this.showErrorMessage('Please select at least one field to update');
      return;
    }

    this.bulkProcessing = true;
    this.adminService
      .bulkUpdateContactSubmissions(
        Array.from(this.selectedSubmissions),
        updates
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.showSuccessMessage(
            `${response.data.successful} submission(s) updated successfully`
          );
          this.closeBulkUpdateModal();
          this.bulkProcessing = false;
          this.clearSelection();
          this.loadSubmissions();
          this.loadStats();
        },
        error: (error) => {
          console.error('18. Bulk update error:', error);
          console.error('19. Error details:', {
            status: error.status,
            message: error.message,
            error: error.error,
          });
          this.showErrorMessage(
            error.message || 'Failed to update submissions'
          );
          this.bulkProcessing = false;
        },
      });
  }

  closeResponseModal(): void {
    this.showResponseModal = false;
    this.selectedSubmissionForResponse = null;
    this.responseText = '';
    this.emailSubject = '';
  }

  closeBulkUpdateModal(): void {
    this.showBulkUpdateModal = false;
    this.bulkUpdateData = {
      status: '',
      priority: '',
      assigned_to: '',
      internal_notes: '',
    };
  }

  closeSubmissionDetailsModal(): void {
    this.showSubmissionDetailsModal = false;
    this.selectedSubmissionDetails = null;
  }

  goBack(): void {
    this.router.navigate(['/admin/dashboard']);
  }

  formatDate(dateString: string): string {
    return this.adminService.formatDate(dateString);
  }

  formatStatus(status: string): string {
    return this.adminService.formatStatus(status);
  }

  formatPriority(priority: string): string {
    return this.adminService.formatPriority(priority);
  }

  getStatusClass(status: string): string {
    return this.adminService.getStatusClass(status);
  }

  getPriorityClass(priority: string): string {
    return this.adminService.getPriorityClass(priority);
  }

  getSelectedCount(): number {
    return this.selectedSubmissions.size;
  }

  onRefresh(): void {
    this.currentPage = 1;
    this.clearSelection();
    this.loadSubmissions();
    this.loadStats();
  }

  trackBySubmissionId(index: number, submission: ContactSubmission): string {
    return submission.id;
  }

  getPageNumbers(): number[] {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(this.totalPages, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }

  shouldShowEllipsis(position: 'start' | 'end'): boolean {
    const maxVisible = 5;
    const halfVisible = Math.floor(maxVisible / 2);

    if (position === 'start') {
      return this.currentPage > halfVisible + 1 && this.totalPages > maxVisible;
    } else {
      return (
        this.currentPage < this.totalPages - halfVisible &&
        this.totalPages > maxVisible
      );
    }
  }

  getAdminName(adminFirstName?: string, adminLastName?: string): string {
    if (adminFirstName && adminLastName) {
      return `${adminFirstName} ${adminLastName}`;
    }
    return 'Unassigned';
  }

  private showSuccessMessage(message: string): void {
    this.messages.success = message;
    this.messages.error = null;
    setTimeout(() => (this.messages.success = null), 5000);
  }

  private showErrorMessage(message: string): void {
    this.messages.error = message;
    this.messages.success = null;
    setTimeout(() => (this.messages.error = null), 8000);
  }

  dismissMessage(type: 'success' | 'error'): void {
    this.messages[type] = null;
  }
}
